import { Box, Skeleton, Typography, pn } from '@compass/core';
import { Column, Summary, TotalItem } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { DevExpressReport } from 'components';
import { useParticipantsData } from '../contexts/ParticipantsDataContext';
import { DashboardCard } from '../components/DashboardCard';
import { ParticipantsFilterControls } from './ParticipantsFilterControls';
import { ParticipantStatusRow } from '../types/ParticipantStatusData';

const ParticipantsTableContent = () => {
    const { tableData, loading, error } = useParticipantsData();
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);
    
    if (loading) {
        return <Skeleton variant="rounded" height={300} />;
    }
    
    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }
    
    if (!tableData.length) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <Typography variant="body2" color="text.secondary">
                    No participants found for selected filters
                </Typography>
            </Box>
        );
    }
    
    return (
        <DevExpressReport
            dashboardStyle
            dataSource={tableData}
            fullPageSize
            showColumnChooser={false}
        >
            <Column
                dataField={pn<ParticipantStatusRow>('participantStatusName')}
                caption="Participation Status"
                width="50%"
            />
            <Column
                dataField={pn<ParticipantStatusRow>('participantCount')}
                caption="Count"
                width="25%"
                alignment="right"
            />
            <Column
                dataField={pn<ParticipantStatusRow>('percentage')}
                caption="Percentage"
                width="25%"
                alignment="right"
                customizeText={(cellInfo) => `${cellInfo.value?.toFixed(1)}%`}
            />
            <Summary>
                <TotalItem
                    column={pn<ParticipantStatusRow>('participantStatusName')}
                    customizeText={() => 'Total'}
                />
                <TotalItem
                    column={pn<ParticipantStatusRow>('participantCount')}
                    summaryType="sum"
                    customizeText={(cellInfo) => String(cellInfo.value)}
                />
                <TotalItem
                    column={pn<ParticipantStatusRow>('percentage')}
                    customizeText={() => '100.0%'}
                />
            </Summary>
        </DevExpressReport>
    );
};

export const ParticipantsTableCard = () => {
    const { tableData, filter, programs } = useParticipantsData();

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (!tableData || tableData.length === 0) return;

        const yearLabel = filter.schoolYear ? `${filter.schoolYear}-${filter.schoolYear + 1}` : 'All Years';
        const programLabel = filter.programId 
            ? programs.find(p => p.id === filter.programId)?.name || 'Unknown Program'
            : 'All Programs';
        const exportDate = new Date().toLocaleDateString();
        
        // Metadata headers
        const metadata = [
            '# Participants Status',
            `# Year: ${yearLabel}`,
            `# Program: ${programLabel}`,
            `# Exported: ${exportDate}`,
            '',
        ];
        
        const headers = 'Participation Status,Count,Percentage';
        
        const rows = tableData.map(item => [
            `"${item.participantStatusName}"`,
            item.participantCount,
            `${item.percentage.toFixed(1)}%`
        ]);

        // Add total row
        const totalCount = tableData.reduce((sum, item) => sum + item.participantCount, 0);
        rows.push(['Total', totalCount, '100.0%']);

        const csvContent = [
            ...metadata,
            headers, 
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const yearFilename = filter.schoolYear || 'AllYears';
        const programName = filter.programId 
            ? programs.find(p => p.id === filter.programId)?.name?.replace(/\s+/g, '_') || 'Program'
            : 'AllPrograms';
        link.setAttribute('download', `Participants_${yearFilename}_${programName}.csv`);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardCard title="Participants Status" onExport={handleExport}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <ParticipantsFilterControls />
                <ParticipantsTableContent />
            </Box>
        </DashboardCard>
    );
};
